import React, { useEffect, useRef, useState } from "react";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import {
  formatToLocalDate,
  getAntdSortOrder,
  useDebounce,
} from "../../../helpers/utils";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import {
  deleteLoadNoticeAPI,
  getCreatedLoadNoticeAPI,
  updateLoadNoticeAPI,
} from "./createdLodNoticeApis";
import { GetOrdersFilterListAPI } from "../../Redux/orders";
import FullScreenLoader from "../../Components/FullScreenLoader";
import NoPermissionPage from "../../Components/NoPermissionPage";
import { svg } from "../../../Assets/svg";
import NotificationButton from "../../Components/NotificationButton";
import NoDataMessage from "../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import { Dropdown, Menu, Select, Spin, Switch, Table, TableColumnsType } from "antd";
import { useSearchParams } from "react-router-dom";
import ClearComponent from "../../Components/ClearComponent";
import { getDecryptedName } from "../../../helpers/bycrypt";
import { CommonConfirmationModal } from "../../../Components/CommonConfirmationModal";
import { generateLoadNoticePdfAPI } from "../Quotations/quotationApis";
import ViewPdfModal from "../Quotations/Components/LoadNotice/ViewPdfModal";

enum LoadNoticeStatus {
  PRINTED = "printed",
  NOT_PRINTED = "not printed",
}

const CreatedLoadNotice: React.FC = () => {
  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData.CREATED_LOAD_NOTICE,
  );

  const { authLoading } = useAppSelector((state: RootState) => state.loading);
  const { CompanyData } = useAppSelector((state: RootState) => state.orders);

  const abortControllerRef = useRef<AbortController | null>(null);

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [loadNoticeList, setLoadNoticeList] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [toggleStatusState, setToggleStatusState] = useState({
    _id: null,
    toPrint: false,
  });
  const [toggling, setToggling] = useState(false);

  // Pagination & Filters
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "";
  const initialSearch =
    searchParams.get("search") || searchParams.get("order_id") || "";

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(30);

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(!!initialStatus);
  const [statusFilter, setStatusFilter] = useState<string>(initialStatus);
  const [selectedCompany, setSelectedCompany] = useState<string>("");

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "asc" | "desc" | null;
  }>({ field: null, order: null });

  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useDebounce(search, 500);

  const toggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  const getFilterCount = () => {
    let count = 0;
    if (statusFilter) count++;
    if (selectedCompany) count++;
    return count;
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!CompanyData || CompanyData.length === 0) {
      dispatch(GetOrdersFilterListAPI());
    }
  }, [dispatch, CompanyData]);

  useEffect(() => {
    const urlSearch =
      searchParams.get("search") || searchParams.get("order_id") || "";
    if (urlSearch && urlSearch !== search) {
      setSearch(urlSearch);
    }
    const urlStatus = searchParams.get("status") || "";
    if (urlStatus && urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
      setIsFilterOpen(true);
    }
  }, [searchParams]);

  //PDF
  const [pdfData, setPdfData] = useState<any>(null);
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);

  const [errorModalData, setErrorModalData] = useState<{
    title: string | null;
    message: string | null;
  }>({
    title: null,
    message: null,
  });

  const fetchCreatedLoadNotice = async (page: number, isReset: boolean) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (isReset) {
      setLoading(true);
    }

    const filters = {
      page: page,
      limit: limit,
      search: debouncedSearch,
      status: statusFilter || undefined,
      customer_id: selectedCompany || undefined,
      ...(sorterState.field && sorterState.order
        ? { order: `${sorterState.field} | ${sorterState.order}` }
        : {}),
    };

    dispatch(
      getCreatedLoadNoticeAPI({
        filters,
        signal: controller.signal,
        onSuccess: (data: any) => {
          const newData = data?.data || [];
          const total = data?.count || 0;
          setTotalCount(total);

          if (isReset) {
            setLoadNoticeList(newData);
            window.scrollTo(0, 0);
          } else {
            setLoadNoticeList((prev) => [...prev, ...newData]);
          }

          const currentCount = isReset
            ? newData.length
            : loadNoticeList.length + newData.length;
          setHasMore(currentCount < total);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchCreatedLoadNotice(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedCompany, statusFilter, sorterState]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchCreatedLoadNotice(nextPage, false);
  };

  const searchBarHandler = (e: any) => setSearch(e.target.value);
  const searchBarClean = () => setSearch("");

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        {USER_PERMISSIONS.REMOVE && (
          <Menu.Item
            key="2"
            onClick={() => {
              setIdToDelete(data?._id);
            }}
            danger
          >
            <div className="d-flex align-items-center gap-2 svg-20">Delete</div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Sr. No.",
      dataIndex: "s_no",
      key: "s_no",
      sorter: true,
      sortOrder: getAntdSortOrder("s_no", sorterState),
      render: (val: any) => val ?? "-",
    },
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
      sortOrder: getAntdSortOrder("order_id", sorterState),
      render: (val: any) => val?.order_id ?? "-",
    },
    {
      title: "Company Name",
      dataIndex: ["customer_id", "company_name"],
      key: "company_name",
      sorter: true,
      sortOrder: getAntdSortOrder("company_name", sorterState),
    },
    {
      title: "Count",
      dataIndex: ["load_notice_id", "qty_loadsign"],
      key: "qty_loadsign",
      sorter: true,
      sortOrder: getAntdSortOrder("qty_loadsign", sorterState),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: getAntdSortOrder("created_at", sorterState),
      render: (val: any) =>
        val !== undefined ? formatToLocalDate(val, true) : "-",
    },
    {
      title: "PDF",
      dataIndex: "pdf",
      key: "pdf",
      render: (val: any, record) => {
        return (
          <>
            <button
              type="button"
              className="btn btn-link p-0 svg-32 d-inline-flex"
              style={{ minHeight: "40px" }}
              disabled={pdfLoadingId === record?._id}
              onClick={() => {
                setPdfLoadingId(record?._id);
                const payload = {
                  quote_id: record?.quote_id as string,
                  load_notice_id: record?.load_notice_id?._id,
                };
                dispatch(
                  generateLoadNoticePdfAPI({
                    payload,
                    onSuccess: (res: any) => {
                      const blob = new Blob([res], {
                        type: "application/pdf",
                      });
                      const url = window.URL.createObjectURL(blob);
                      setPdfData({
                        url,
                        blob,
                        title: `Load Notice PDF`,
                      });
                      setPdfLoadingId(null);
                    },
                    onError: () => {
                      setPdfLoadingId(null);
                    },
                    onMissingDetailsError: (messageData: any) => {
                      if (messageData?.description && messageData.title) {
                        setErrorModalData({
                          title: messageData.title,
                          message: messageData.description,
                        });
                      }
                    },
                  }),
                );
              }}
            >
              <div className="d-flex align-items-center justify-content-center">
                {pdfLoadingId === record?._id ? (
                  <Spin size="small" />
                ) : (
                  <div className="d-flex gap-1 align-items-center">
                    {svg?.loadNoticePDF}
                    <span className="text-black text-decoration-underline">Download</span>
                  </div>
                )}
              </div>
            </button>
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: getAntdSortOrder("status", sorterState),
      width: "15%",
      render: (val: any, record) => {
        const status = val as LoadNoticeStatus;
        return (
          <div className="d-flex gap-1 align-items-center">
            <Switch
              className="customSwitch"
              checked={status === LoadNoticeStatus.PRINTED}
              //   checkedChildren="Printed"
              //   unCheckedChildren="Not Printed"
              onChange={(checked) => {
                setToggleStatusState({
                  _id: record?._id,
                  toPrint: !!checked ? true : false,
                });
              }}
            />
            {status === LoadNoticeStatus?.PRINTED ? (
              <div>Printed</div>
            ) : (
              <div className="text-secondary">Not Printed</div>
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="d-flex" onClick={(e) => e.stopPropagation()}>
          {USER_PERMISSIONS.REMOVE && (
            <Dropdown
              overlay={() => MoreOptionsMenu(record)}
              trigger={["click"]}
              placement="bottomRight"
              className="cursor-pointer"
              overlayClassName="moreOptionDropdown"
            >
              <span className="svg-32 svg-outline-primary" role="button">
                {svg?.tableMoreOption}
              </span>
            </Dropdown>
          )}
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    // Convert AntD 'ascend'/'descend' to Backend 'asc'/'desc'
    const order =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : null;
    let field = sorter.field;
    if (Array.isArray(field)) {
      field = field[field.length - 1];
    }

    setSorterState({ field, order: order as any });
  };

  return USER_PERMISSIONS.READ ? (
    <>
      <div className={`page-content h-100 px-40 py-40 d-flex flex-column ${isMobile ? "pb-0" : ""}`}>
        <div className="mb-sm-3 mb-2 d-flex flex-column gap-md-2 gap-0 flex-shrink-0">
          <div className="d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-between align-items-center">
            <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-md-1 order-1">
              Created Load Notice ({totalCount})
            </h2>
            <div
              className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-md-2 order-4 mobileFilter`}
            >
              <div className="pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2 mob-w-100">
                <input
                  type="text"
                  value={search}
                  className="form-control form-control-lg"
                  onChange={searchBarHandler}
                  placeholder="Search"
                />
                {search?.length ? (
                  <span className="cursor-pointer" onClick={searchBarClean}>
                    {svg?.searchClear}
                  </span>
                ) : (
                  <span>{svg?.search}</span>
                )}
              </div>
            </div>
            <div className="d-flex gap-sm-3 gap-2 order-md-3 order-3">
              <div
                onClick={toggleFilter}
                role="button"
                aria-expanded={isFilterOpen}
                className="filterButton position-relative ms-sm-0 ms-auto"
              >
                {svg?.filterIcon}
                {getFilterCount() > 0 && (
                  <span className="position-absolute badge customBadgeFont rounded-pill bg-danger">
                    {getFilterCount()}
                  </span>
                )}
              </div>
              <NotificationButton />
            </div>
          </div>
          <div
            className={`filter-transition-container overflow-hidden ${
              isFilterOpen ? "is-open" : ""
            }`}
          >
            <div className="d-flex flex-wrap align-items-end bg-white p-2 rounded-3 mt-2 position-relative">
              <label className="filterTitle fw-bold fs-5 position-absolute d-flex align-items-center">
                Filters
              </label>
              <div className="row gx-md-3 gx-2 gy-sm-3 gy-2">
                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Status
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      value={statusFilter}
                      className="form-select form-select-lg"
                      onChange={(value) => {
                        setStatusFilter(value);
                      }}
                    >
                      <Select.Option value="">All</Select.Option>
                      <Select.Option value={LoadNoticeStatus.NOT_PRINTED}>
                        Not Printed
                      </Select.Option>
                      <Select.Option value={LoadNoticeStatus.PRINTED}>
                        Printed
                      </Select.Option>
                    </Select>
                  </div>
                </div>

                <div className="col-lg-auto col-md-4 col-sm-6">
                  <label className="fs-6 mob-fs-6 fw-semibold text-secondary me-2">
                    Company
                  </label>
                  <div className="whatsBannedDropdown filterCommonDropdown mw-100 d-inline-block">
                    <Select
                      value={selectedCompany}
                      className="form-select form-select-lg"
                      onChange={(value) => {
                        setSelectedCompany(value);
                      }}
                    >
                      <Select.Option value="">All</Select.Option>
                      {CompanyData?.map((elem: any) => {
                        return (
                          <Select.Option key={elem?._id} value={elem?._id}>
                            {elem?.company_name}
                          </Select.Option>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                {statusFilter || selectedCompany || search ? (
                  <ClearComponent
                    handleClick={() => {
                      setStatusFilter("");
                      setSelectedCompany("");
                      setSearch("");
                      setIsFilterOpen(false);
                      if (searchParams.get("status") || searchParams.get("search")) {
                        setSearchParams({});
                      }
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`card page-table-card page-table-device border-0 shadow rounded-4 flex-fill ${
            isMobile && isFilterOpen ? "" : "overflow-y-auto"
          }`}
          style={{ minHeight: 0 }}
        >
          <div className="card-body overflow-y-auto" id="scrollableDiv">
            {loading && currentPage === 1 ? (
              <FullScreenLoader />
            ) : loadNoticeList.length === 0 ? (
              <NoDataMessage
                icon={svg?.emptyCreatedLoadNoticeIcon}
                text="You do not have any load notice yet."
              />
            ) : (
              <InfiniteScroll
                dataLength={loadNoticeList.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  loading && (
                    <div className="text-center p-2">
                      <Spin />
                    </div>
                  )
                }
                scrollableTarget="scrollableDiv"
                height={"100%"}
                style={{ overflow: "auto", paddingBottom: isMobile ? 16 : 0 }}
              >
                {!isMobile ? (
                  <Table
                    dataSource={loadNoticeList}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey={(record) => record._id}
                    className="custom-table"
                    bordered
                  />
                ) : (
                  <div className="px-3">
                    <div className="row gx-3 gy-3">
                      {loadNoticeList.map((record: any, index: number) => {
                        const sNo = record.s_no ?? index + 1;
                        const orderId = record.order_id?.order_id ?? "-";
                        const companyName =
                          record.customer_id?.company_name || "-";
                        const count =
                          record.load_notice_id?.qty_loadsign ?? "-";
                        const createdAt = record.created_at
                          ? formatToLocalDate(record.created_at, true)
                          : "-";
                        const status = record.status as LoadNoticeStatus;

                        return (
                          <div
                            className="col-md-6"
                            key={record._id || index}
                          >
                            <div className="card mobile-table-card mobile-font-s rounded-4 p-3">
                              {/* Card Header: Order ID & Actions / Sr No */}
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="fw-bold fs-5">
                                  #{orderId}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-light text-dark border">
                                    Sr: {sNo}
                                  </span>
                                  {USER_PERMISSIONS.REMOVE && (
                                    <div
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Dropdown
                                        overlay={() =>
                                          MoreOptionsMenu(record)
                                        }
                                        trigger={["click"]}
                                        placement="bottomRight"
                                        overlayClassName="moreOptionDropdown"
                                      >
                                        <span className="cursor-pointer">
                                          {svg?.tableMobileOption}
                                        </span>
                                      </Dropdown>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Company Name */}
                              <h6 className="fw-bold fs-5 mb-2 text-break">
                                {companyName}
                              </h6>

                              {/* Details Grid */}
                              <div className="mobile-card-details fs-6 fs-6-mob">
                                <div className="row gy-1">
                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Count:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {count}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <div className="row">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Created At:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        {createdAt}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <div className="row align-items-center">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        PDF:
                                      </div>
                                      <div className="col-sm-7 col-7">
                                        <button
                                          type="button"
                                          className="btn btn-link p-0 svg-32 d-inline-flex"
                                          style={{ minHeight: "32px" }}
                                          disabled={
                                            pdfLoadingId === record?._id
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPdfLoadingId(record?._id);
                                            const payload = {
                                              quote_id: record?.quote_id as string,
                                              load_notice_id:
                                                record?.load_notice_id?._id,
                                            };
                                            dispatch(
                                              generateLoadNoticePdfAPI({
                                                payload,
                                                onSuccess: (res: any) => {
                                                  const blob = new Blob(
                                                    [res],
                                                    {
                                                      type: "application/pdf",
                                                    },
                                                  );
                                                  const url =
                                                    window.URL.createObjectURL(
                                                      blob,
                                                    );
                                                  setPdfData({
                                                    url,
                                                    blob,
                                                    title: `Load Notice PDF`,
                                                  });
                                                  setPdfLoadingId(null);
                                                },
                                                onError: () => {
                                                  setPdfLoadingId(null);
                                                },
                                                onMissingDetailsError: (
                                                  messageData: any,
                                                ) => {
                                                  if (
                                                    messageData?.description &&
                                                    messageData.title
                                                  ) {
                                                    setErrorModalData({
                                                      title:
                                                        messageData.title,
                                                      message:
                                                        messageData.description,
                                                    });
                                                  }
                                                },
                                              }),
                                            );
                                          }}
                                        >
                                          <div className="d-flex align-items-center justify-content-center">
                                            {pdfLoadingId ===
                                            record?._id ? (
                                              <Spin size="small" />
                                            ) : (
                                              <div className="d-flex gap-1 align-items-center">
                                                {svg?.loadNoticePDF}
                                                <span className="text-black text-decoration-underline">
                                                  Download
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <div className="row align-items-center">
                                      <div className="col-sm-5 col-5 text-secondary fw-medium">
                                        Status:
                                      </div>
                                      <div
                                        className="col-sm-7 col-7"
                                        onClick={(e) =>
                                          e.stopPropagation()
                                        }
                                      >
                                        <div className="d-flex gap-1 align-items-center">
                                          <Switch
                                            className="customSwitch"
                                            checked={
                                              status ===
                                              LoadNoticeStatus.PRINTED
                                            }
                                            onChange={(checked) => {
                                              setToggleStatusState({
                                                _id: record?._id,
                                                toPrint: !!checked
                                                  ? true
                                                  : false,
                                              });
                                            }}
                                          />
                                          {status ===
                                          LoadNoticeStatus?.PRINTED ? (
                                            <div>Printed</div>
                                          ) : (
                                            <div className="text-secondary">
                                              Not Printed
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </InfiniteScroll>
            )}
          </div>
        </div>
      </div>

      <CommonConfirmationModal
        open={toggleStatusState?._id !== null}
        title={
          !!toggleStatusState?.toPrint
            ? "Mark Printed, Are You Sure?"
            : "Mark Not Printed, Are You Sure?"
        }
        subTitle={
          !!toggleStatusState?.toPrint
            ? "Are you sure you want to mark this document as printed? This will update its status to “Printed.”"
            : "Are you sure you want to mark this document as not printed? This will update its status to “Not Printed.”"
        }
        loading={toggling}
        confirmButtonColorClass="btn-primary"
        confirmButtonText={
          !!toggleStatusState?.toPrint ? "Mark Printed" : "Mark Not Printed"
        }
        onSuccess={() => {
          setToggling(true);
          const payload = {
            id: toggleStatusState?._id,
            status: !!toggleStatusState?.toPrint
              ? LoadNoticeStatus.PRINTED
              : LoadNoticeStatus.NOT_PRINTED,
          };
          dispatch(
            updateLoadNoticeAPI({
              payload,
              onSuccess: (data) => {
                console.log("Updated Data: ", data);
                setLoadNoticeList((prev) => {
                  return prev.map((item) => {
                    if (item?._id === data?._id) {
                      return {
                        ...item,
                        status: data?.status,
                      };
                    }
                    return item;
                  });
                });
                setToggling(false);
                setToggleStatusState({ _id: null, toPrint: false });
              },
              onError: () => {
                setToggling(false);
              },
            }),
          );
        }}
        onClose={() => {
          setToggleStatusState({ _id: null, toPrint: false });
        }}
      />

      <ViewPdfModal
        open={pdfData != null}
        pdfData={pdfData}
        onClose={() => {
          if (pdfData?.url) window.URL.revokeObjectURL(pdfData.url);
          setPdfData(null);
        }}
      />

      {errorModalData?.title && (
        <CommonConfirmationModal
          open={errorModalData?.title !== null}
          onClose={() => {
            setErrorModalData({ title: null, message: null });
          }}
          onSuccess={() => setErrorModalData({ title: null, message: null })}
          title={errorModalData?.title || ""}
          subTitle={errorModalData?.message || ""}
          onlyCloseButton={true}
        />
      )}

      <CommonConfirmationModal
        open={idToDelete != null}
        title="Remove Load Notice, Are You Sure?"
        subTitle="Removing this load notice will delete it permanently from the list. This action cannot be undone. Please confirm before proceeding."
        loading={deleting}
        onSuccess={() => {
          if (!idToDelete) return;
          dispatch(
            deleteLoadNoticeAPI({
              id: idToDelete,
              onSuccess: () => {
                fetchCreatedLoadNotice(1, true);
                setDeleting(false);
                setIdToDelete(null);
              },
              onError: () => {
                setDeleting(false);
              },
            }),
          );
        }}
        onClose={() => setIdToDelete(null)}
        confirmButtonText="REMOVE"
      />
    </>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default CreatedLoadNotice;
